import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, ArrowLeft, Sparkles, MapPin, LockKeyhole } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CheckoutPaymentMethods from "./CheckoutPaymentMethods";
import CheckoutOrderSummary from "./CheckoutOrderSummary";
import CheckoutLoyaltySection from "./CheckoutLoyaltySection";
import CheckoutUpsellSection from "./CheckoutUpsellSection";
import CheckoutScheduleSection from "./CheckoutScheduleSection";
import CheckoutCouponSection from "./CheckoutCouponSection";
import { API_URL } from "@/utils/apiConfig";
const _BACKEND_URL = API_URL || "";
const _isProxiedDomain = (() => {
  try {
    const backendHost = new URL(_BACKEND_URL).hostname;
    const currentHost = window.location.hostname;
    if (backendHost.endsWith(`.${currentHost}`)) return false;
    return currentHost !== backendHost && !currentHost.includes('localhost') && !currentHost.includes('127.0.0.1');
  } catch { return false; }
})();
const API = _isProxiedDomain ? "/api" : `${_BACKEND_URL}/api`;
/**
 * Step 2: Full order form.
 * Left column: schedule, coupon, payment, customer info, upsell suggestions.
 * Right column: cart summary.
 */
export default function CheckoutStep2({
  tc, restaurant, restaurantId, fmt, isTableOrder, isOpen, table,
  cart, cartTotal, categories, onUpdateQuantity, smartPricing,
  schedule, onScheduleChange, getAvailableDates, getTimeSlots,
  coupon, onCouponChange,
  loyalty, onLoyaltyChange,
  payment, onPaymentChange,
  customer, onCustomerChange,
  delivery, onDeliveryChange,
  orderType,
  suggestions, onAddSuggestion, onRemoveSuggestion, onTrackUpsell,
  submitting, onSubmit,
  onBack,
}) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [pr, pg, pb] = tc.primary.replace("#","").match(/.{2}/g).map(h => parseInt(h,16));
  const [availableRewards, setAvailableRewards] = useState([]);
  const [loadingRewards, setLoadingRewards] = useState(false);
  const [loyaltyCustomer, setLoyaltyCustomer] = useState(null);
  const [savedAddress, setSavedAddress] = useState(null); // { address, address_number, ... }
  const [addressApplied, setAddressApplied] = useState(false);
  useEffect(() => {
    if (coupon.discount?.valid && coupon.discount?.online_only && payment.method !== "online") {
      onPaymentChange.method("online");
    }
  }, [coupon.discount, onPaymentChange, payment.method]);
  // Fetch loyalty + saved address when phone has 9+ digits
  useEffect(() => {
    const phone = customer.phone?.replace(/\D/g, "");
    if (!phone || phone.length < 9) {
      setAvailableRewards([]); setLoyaltyCustomer(null); setSavedAddress(null); setAddressApplied(false);
      return;
    }
    const prefix = restaurant?.phone_prefix || "+34";
    const fullPhone = `${prefix}${phone}`;
    setLoadingRewards(true);
    const loyaltyReq = axios.get(`${API}/public/loyalty/customer/${encodeURIComponent(fullPhone)}?restaurant_id=${restaurant?.id || restaurantId}`)
      .then(res => {
        const rewards = res.data?.available_rewards || [];
        setAvailableRewards(rewards);
        if (searchParams.get("use_loyalty") === "1" && !isTableOrder && !loyalty?.reward && rewards[0]) onLoyaltyChange(rewards[0]);
        if (res.data?.points != null) {
          setLoyaltyCustomer({ points: res.data.points, name: res.data.name || null });
          // Auto-fill name if field is empty
          if (!customer.name && res.data.name) onCustomerChange.name(res.data.name);
        } else {
          setLoyaltyCustomer(null);
        }
      })
      .catch(() => { setAvailableRewards([]); setLoyaltyCustomer(null); });
    const addressReq = orderType === "delivery"
      ? axios.get(`${API}/public/customer/last-address?phone=${encodeURIComponent(fullPhone)}&restaurant_id=${restaurant?.id || restaurantId}`)
          .then(res => {
            if (res.data?.found && res.data.address) {
              setSavedAddress(res.data);
            } else {
              setSavedAddress(null);
            }
          })
          .catch(() => setSavedAddress(null))
      : Promise.resolve();
    Promise.all([loyaltyReq, addressReq]).finally(() => setLoadingRewards(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer.phone, restaurant?.id, restaurantId, restaurant?.phone_prefix, orderType]);
  const applyLastAddress = () => {
    if (!savedAddress || !onDeliveryChange) return;
    onDeliveryChange.address(savedAddress.address);
    onDeliveryChange.number(savedAddress.address_number || "");
    onDeliveryChange.complement(savedAddress.address_complement || "");
    onDeliveryChange.barrio(savedAddress.address_barrio || "");
    if (savedAddress.lat) onDeliveryChange.lat(savedAddress.lat);
    if (savedAddress.lng) onDeliveryChange.lng(savedAddress.lng);
    setAddressApplied(true);
    toast.success(t("checkout.addressApplied"));
  };
  const tableLoyaltyBlocked = isTableOrder && new Date().getDay() === 3;
  // Restaurant can disable name/phone collection for table orders (anonymous QR ordering)
  const requireCustomerForTable = restaurant?.require_customer_info_table !== false;
  const showCustomerInfo = !isTableOrder || requireCustomerForTable;
  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2">
          {!isTableOrder && (
            <Button variant="ghost" size="icon" onClick={onBack}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
          )}
          <DialogTitle className="text-lg font-bold">{t("checkout.title")}</DialogTitle>
        </div>
      </DialogHeader>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Left column */}
        <div className="lg:col-span-3 space-y-4">
          {/* Schedule */}
          <CheckoutScheduleSection
            tc={tc}
            isOpen={isOpen}
            isTableOrder={isTableOrder}
            schedule={schedule}
            onScheduleChange={onScheduleChange}
            getAvailableDates={getAvailableDates}
            getTimeSlots={getTimeSlots}
            restaurant={restaurant}
          />
          {/* Coupon */}
          <CheckoutCouponSection
            tc={tc}
            coupon={coupon}
            onCouponChange={onCouponChange}
            restaurant={restaurant}
            restaurantId={restaurantId}
            customer={customer}
            apiUrl={API}
            cart={cart}
            cartTotal={cartTotal}
            orderType={orderType}
            paymentMethod={payment.method}
          />
          {/* Loyalty rewards */}
          {!(coupon.discount?.valid && coupon.discount?.kind === "coupon") && (
            tableLoyaltyBlocked ? (
              <div className="rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 p-4" data-testid="table-loyalty-wednesday-block">
                <div className="flex items-start gap-3">
                  <LockKeyhole className="w-5 h-5 mt-0.5 text-amber-700 shrink-0" />
                  <div>
                    <p className="text-sm font-black text-amber-900">{t("checkout.tableLoyaltyPausedTitle")}</p>
                    <p className="text-xs text-amber-800 mt-1">{t("checkout.tableLoyaltyPausedDesc")}</p>
                  </div>
                </div>
              </div>
            ) : (
              <CheckoutLoyaltySection tc={tc} availableRewards={availableRewards} loyalty={loyalty} onLoyaltyChange={onLoyaltyChange} />
            )
          )}
          {/* Payment methods */}
          {!isTableOrder && (
            <CheckoutPaymentMethods
              restaurant={restaurant} tc={tc} fmt={fmt} cartTotal={cartTotal}
              payment={payment} onPaymentChange={onPaymentChange}
              isTableOrder={isTableOrder} table={table} couponPolicy={coupon.discount} />
          )}
          {isTableOrder && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="table-pay-at-end-notice">
              <p className="text-sm font-semibold text-emerald-800">{t("checkout.payAtEnd")}</p>
              <p className="text-xs text-emerald-600 mt-1">{t("checkout.payAtEndDesc")}</p>
            </div>
          )}
          {/* Customer info — hidden for table orders when restaurant disables it */}
          {showCustomerInfo && <div className="space-y-2">
            <Input value={customer.name} onChange={e => onCustomerChange.name(e.target.value)} placeholder={t("checkout.yourName")} className="bg-gray-50" data-testid="customer-name" />
            <div className="flex">
              <span className="inline-flex items-center px-2.5 rounded-l-md border border-r-0 border-gray-300 bg-gray-100 text-sm font-bold text-gray-700 select-none" data-testid="public-phone-prefix">
                {restaurant?.phone_prefix || "+34"}
              </span>
              <Input value={customer.phone} onChange={e => { const val = e.target.value.replace(/[^\d\s]/g, ""); onCustomerChange.phone(val); }} placeholder="612 345 678" className="bg-gray-50 rounded-l-none" data-testid="customer-phone" />
            </div>
            {/* Saved address banner — delivery only, never for table orders */}
            {!isTableOrder && orderType === "delivery" && savedAddress && !addressApplied && (
              <div className="rounded-xl p-3 border-2 flex items-start gap-3" style={{ borderColor: `${tc.primary}60`, backgroundColor: `${tc.primary}08` }} data-testid="saved-address-banner">
                <MapPin className="w-4 h-4 shrink-0 mt-0.5" style={{ color: tc.primary }} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold" style={{ color: tc.primary }}>{t("checkout.savedAddress")}</p>
                  <p className="text-xs text-gray-600 mt-0.5 truncate">{savedAddress.full_address || savedAddress.address}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" className="h-7 text-xs text-white px-3" style={{ backgroundColor: tc.primary }} onClick={applyLastAddress} data-testid="use-saved-address-btn">
                    {t("checkout.useAddress")}
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={onBack} data-testid="change-address-btn">
                    {t("checkout.changeAddress")}
                  </Button>
                </div>
              </div>
            )}
            {/* Applied address confirmation — delivery only, never for table orders */}
            {!isTableOrder && orderType === "delivery" && addressApplied && delivery?.address && (
              <div className="rounded-xl p-3 border flex items-center gap-3 bg-emerald-50 border-emerald-200" data-testid="applied-address-banner">
                <MapPin className="w-4 h-4 text-emerald-600 shrink-0" />
                <p className="text-xs text-emerald-800 flex-1 truncate">{delivery.full || delivery.address}</p>
                <Button size="sm" variant="ghost" className="h-7 text-xs text-gray-500 px-2 shrink-0" onClick={() => { setAddressApplied(false); onBack(); }}>
                  {t("checkout.changeAddress")}
                </Button>
              </div>
            )}
            {/* Customer recognition banner */}
            {loyaltyCustomer && (
              <div className="rounded-xl p-3 border-2 flex items-center gap-3" style={{ borderColor: `${tc.primary}50`, backgroundColor: `${tc.primary}08` }} data-testid="loyalty-recognition-banner">
                <Sparkles className="w-5 h-5 shrink-0" style={{ color: tc.primary }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold" style={{ color: tc.primary }}>
                    {t("checkout.loyaltyGreeting", {
                      name: loyaltyCustomer.name ? loyaltyCustomer.name.split(" ")[0] : "",
                    })} 👋
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    {t("checkout.loyaltyPoints", { points: loyaltyCustomer.points })}
                    {tableLoyaltyBlocked
                      ? ` — ${t("checkout.loyaltyAccumulateOnly")}`
                      : (availableRewards.length > 0
                        ? ` — ${t("checkout.loyaltyUseNow")}`
                        : ".")}
                  </p>
                </div>
                {loadingRewards && <Loader2 className="w-4 h-4 animate-spin opacity-50" />}
              </div>
            )}
          </div>}
          <Textarea value={customer.notes} onChange={e => onCustomerChange.notes(e.target.value)} placeholder={t("checkout.orderNotes")} className="bg-gray-50" rows={2} />
          {/* Upsell suggestions */}
          <CheckoutUpsellSection
            tc={tc}
            fmt={fmt}
            suggestions={suggestions}
            categories={categories}
            onAddSuggestion={onAddSuggestion}
            onRemoveSuggestion={onRemoveSuggestion}
            onTrackUpsell={onTrackUpsell}
          />
        </div>
        {/* Right column: cart summary */}
        <div className="lg:col-span-2">
          <CheckoutOrderSummary
            tc={tc} fmt={fmt} cart={cart} cartTotal={cartTotal} smartPricing={smartPricing}
            categories={categories}
            isTableOrder={isTableOrder} table={table} delivery={delivery} orderType={orderType} restaurant={restaurant}
            payment={payment} onUpdateQuantity={onUpdateQuantity}
            loyaltyReward={loyalty} couponDiscount={coupon.discount} />
        </div>
      </div>
      <DialogFooter>
        <Button
          onClick={onSubmit}
          disabled={submitting || (showCustomerInfo && !customer.name)}
          className="w-full h-12 text-white font-bold text-sm uppercase rounded-xl"
          style={{ backgroundColor: tc.primary }}
          data-testid="confirm-order-btn">
          {submitting
            ? <Loader2 className="w-5 h-5 animate-spin" />
            : t("checkout.placeOrder")}
        </Button>
      </DialogFooter>
    </>
  );
}
